// src/App.js
import { useState, useEffect } from "react";
import Profile from "./components/Profile";
import BrandingBadge from "./components/BrandingBadge";
import "./styles/main.css";
import { getUserData } from "./services/api";
import {
  beginsWithVowel,
  getUsernameFromURL,
  isCustomDomain,
} from "./services/helperFunctions";
import config from "./config";
import freeThemes from "./data/freeThemes.mjs";

const App = () => {
  const [template, setTemplate] = useState("default");
  const [profileData, setProfileData] = useState(null);

  const username = getUsernameFromURL();

  const headName = profileData?.displayname || username;
  const role = profileData?.featured_roles?.[0] || "";
  const location = profileData?.location?.text || "";

  const title = `${headName} - ${role || "Twine"}`;
  let metaDescription = `One-page site for ${headName}. `;

  if (role && location) {
    metaDescription += `${headName} is ${
      beginsWithVowel(role) ? "an" : "a"
    } ${role} from ${location}.`;
  } else if (role) {
    metaDescription += `${headName} is ${
      beginsWithVowel(role) ? "an" : "a"
    } ${role} on twine.net.`;
  } else if (location) {
    metaDescription += `${headName} is a freelancer from ${location} on twine.net.`;
  } else {
    metaDescription += `${headName} is a freelancer on twine.net.`;
  }

  useEffect(() => {
    getUserData(username).then((data) => {
      if (!data.active) {
        window.location.href = "https://www.twine.net";
        return;
      }

      // Check if user is on custom domain but not pro - redirect to twinesite.com
      if (isCustomDomain() && !data.pro) {
        window.location.href = `https://${username}.twinesite.com${window.location.pathname}${window.location.search}`;
        return;
      }

      setProfileData(data);
      const urlParams = new URLSearchParams(window.location.search);
      const urlTemplate = urlParams.get("template");
      setTemplate(urlTemplate || data?.website?.theme || null);
    });
  }, [username]);

  useEffect(() => {
    if (template) {
      let templateToUse = template;

      // Force default theme for non-pro users trying to use premium themes
      if (profileData && !profileData.pro && !freeThemes.includes(template)) {
        templateToUse = "default";
      }

      import(`./styles/templates/${templateToUse}.css`).catch(() => {
        import("./styles/templates/default.css");
      });
    }
  }, [template, profileData]);

  return (
    <div className={`app`}>
      {/* Head */}
      <title>{title}</title>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap"
      />
      <link rel="canonical" href={`${config.baseUrl.replace("*", username)}`} />
      <meta name="description" content={metaDescription} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={`${config.baseUrl.replace("*", username)}`} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />

      {/* Body */}
      {!profileData ? (
        <div></div>
      ) : (
        <div className="fade-in">
          <Profile profileData={profileData} />
          {(profileData.show_branding || !profileData.pro) && <BrandingBadge />}
        </div>
      )}
    </div>
  );
};

export default App;
